/**
 * TaxRateEditor — Adókulcsok inline szerkesztő
 * PAY-12: éves adókulcsok listázása + inline szerkesztés
 */

import { useApiError } from '@/hooks/use-api-error';
import { useTranslation } from '@kgc/i18n';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  toast,
} from '@kgc/ui';
import { useState } from 'react';
import { useDeleteTaxRate } from '../hooks/useDeleteTaxRate';
import type { TaxRateDto } from '../hooks/useTaxRates';
import { useTaxRates } from '../hooks/useTaxRates';
import { useUpdateTaxRate } from '../hooks/useUpdateTaxRate';

/** Magyar cimkék az adónem enum értékekhez */
const TAX_TYPE_LABELS: Record<string, string> = {
  PERSONAL_INCOME_TAX: 'SZJA',
  EMPLOYEE_SOCIAL_TAX: 'TB járulék (munkavállaló)',
  EMPLOYER_SOCIAL_TAX: 'SZOCHO (munkáltató)',
  KIVA_TAX: 'KIVA',
  VOCATIONAL_FUND: 'Szakképzési hozzájárulás',
  CAFETERIA_TAX: 'SZÉP cafeteria közteher',
  OVERTIME_MULTIPLIER_150: 'Túlóra szorzó 150%',
  OVERTIME_MULTIPLIER_200: 'Túlóra szorzó 200%',
  NIGHT_SHIFT_PREMIUM: 'Éjszakai pótlék',
  AFTERNOON_SHIFT_PREMIUM: 'Délutáni pótlék',
  SICK_PAY_RATE: 'Betegszabadság díj',
};

const CURRENT_YEAR = new Date().getFullYear();
const YEARS = [CURRENT_YEAR - 1, CURRENT_YEAR, CURRENT_YEAR + 1];

interface RowEdit {
  rate: string;
  description: string;
}

function formatRate(rate: number): string {
  // Percentage display: 0.15 → "15%", 1.5 → "150%" (szorzók)
  if (rate >= 1) {
    return `${(rate * 100).toFixed(0)}%`;
  }
  return `${(rate * 100).toFixed(1)}%`;
}

function parseRateInput(raw: string): number | null {
  // Accept "0.15" or "15" or "15%" → convert to decimal fraction
  const cleaned = raw.trim().replace('%', '');
  const n = parseFloat(cleaned);
  if (isNaN(n)) return null;
  // Heuristic: if > 5 it's probably a percentage
  return n > 5 ? n / 100 : n;
}

export function TaxRateEditor() {
  const { t } = useTranslation();
  const { getErrorMessage } = useApiError();
  const [year, setYear] = useState<number>(CURRENT_YEAR);
  const { data: rates, isLoading, error } = useTaxRates(year);
  const updateMutation = useUpdateTaxRate();
  const deleteMutation = useDeleteTaxRate();

  // Per-row local edit state: id → { rate, description }
  const [edits, setEdits] = useState<Record<string, RowEdit>>({});
  // Track which rows are dirty
  const [dirty, setDirty] = useState<Set<string>>(new Set());

  function startEdit(row: TaxRateDto) {
    if (edits[row.id] !== undefined) return;
    setEdits(prev => ({
      ...prev,
      [row.id]: {
        rate: String(row.rate),
        description: row.description ?? '',
      },
    }));
  }

  function handleFieldChange(id: string, field: keyof RowEdit, value: string) {
    setEdits(prev => ({ ...prev, [id]: { ...prev[id]!, [field]: value } }));
    setDirty(prev => new Set(prev).add(id));
  }

  async function handleSave(id: string) {
    const edit = edits[id];
    if (!edit) return;
    const parsedRate = parseRateInput(edit.rate);
    if (parsedRate === null) return;
    try {
      await updateMutation.mutateAsync({
        id,
        patch: {
          rate: parsedRate,
          description: edit.description || undefined,
        },
      });
    } catch (err) {
      // A rejection eddig NÉMÁN szakította meg a függvényt: a hiba nem jelent meg sehol,
      // és a lenti cleanup sem futott le — a sor szerkesztő-módban ragadt. Most a hiba
      // látható, a bevitel pedig SZÁNDÉKOSAN megmarad (a korai return miatt), hogy a
      // felhasználó javíthasson és újrapróbálhasson.
      toast.error(getErrorMessage(err, t('payroll.config.saveError')));
      return;
    }
    setDirty(prev => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
    setEdits(prev => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  }

  async function handleDelete(id: string) {
    if (!window.confirm(t('payroll.config.deleteConfirm'))) return;
    try {
      await deleteMutation.mutateAsync(id);
    } catch (err) {
      toast.error(getErrorMessage(err, t('payroll.config.deleteError')));
      return;
    }
    setEdits(prev => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-muted-foreground">
            {t('payroll.config.year')}:
          </span>
          <Select value={String(year)} onValueChange={v => setYear(Number(v))}>
            <SelectTrigger className="w-28">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {YEARS.map(y => (
                <SelectItem key={y} value={String(y)}>
                  {y}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading && <p className="text-sm text-muted-foreground py-4">{t('common.loading')}</p>}
        {error && <p className="text-sm text-destructive py-4">{t('payroll.config.loadError')}</p>}
        {rates && rates.length === 0 && (
          <p className="text-sm text-muted-foreground py-4">{t('payroll.config.noRatesForYear')}</p>
        )}
        {rates && rates.length > 0 && (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('payroll.config.taxType')}</TableHead>
                <TableHead className="w-32">{t('payroll.config.rate')}</TableHead>
                <TableHead>{t('payroll.config.description')}</TableHead>
                <TableHead className="w-24">{t('payroll.config.validFrom')}</TableHead>
                <TableHead className="w-24">{t('payroll.config.validTo')}</TableHead>
                <TableHead className="w-28 text-right">{t('common.actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rates.map(row => {
                const edit = edits[row.id];
                const isDirty = dirty.has(row.id);
                const isSaving = updateMutation.isPending;
                const isDeleting = deleteMutation.isPending;
                return (
                  <TableRow key={row.id} onClick={() => startEdit(row)} className="cursor-pointer">
                    <TableCell className="font-medium">
                      {TAX_TYPE_LABELS[row.taxType] ?? row.taxType}
                    </TableCell>
                    <TableCell>
                      {edit !== undefined ? (
                        <input
                          type="text"
                          value={edit.rate}
                          onClick={e => e.stopPropagation()}
                          onChange={e => handleFieldChange(row.id, 'rate', e.target.value)}
                          className="w-full rounded border px-2 py-1 text-sm"
                        />
                      ) : (
                        <span className="text-sm">{formatRate(row.rate)}</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {edit !== undefined ? (
                        <input
                          type="text"
                          value={edit.description}
                          onClick={e => e.stopPropagation()}
                          onChange={e => handleFieldChange(row.id, 'description', e.target.value)}
                          className="w-full rounded border px-2 py-1 text-sm"
                        />
                      ) : (
                        <span className="text-sm text-muted-foreground">
                          {row.description ?? '—'}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {row.validFrom.slice(0, 10)}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {row.validTo.slice(0, 10)}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1" onClick={e => e.stopPropagation()}>
                        {isDirty && (
                          <Button
                            size="sm"
                            variant="default"
                            disabled={isSaving}
                            onClick={() => void handleSave(row.id)}
                          >
                            {t('payroll.config.save')}
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="destructive"
                          disabled={isDeleting}
                          onClick={() => void handleDelete(row.id)}
                        >
                          {t('payroll.config.delete')}
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
